import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, NgZone, output } from "@angular/core";
import { DashLangService } from "../../../../modules/home/dash/service/dash.lang.service";
import { s25Resource } from "../../../../util/s25.resource";
import { DashSectionToolboxBridge } from "../../../../modules/home/dash/service/dash.section.toolbox.bridge";
import { AlertService } from "../../../../services/alert.service";
import type { HomeDashCustomize } from "../panel/home.dash.panel.model";
import type { HomeDashSectionModel } from "./home.dash.section.model";
import { HomeDashSectionRegularComponent } from "./home.dash.section.regular.component";

@Component({
    selector: "s25-ng-dash-section-toolbox",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, HomeDashSectionRegularComponent],
    template: `
        <div class="dash-section-toolbox" id="dashSectionToolbox">
            @if (customize().toolboxOpen() && dashLangResource.value(); as dashLang) {
                <div [attr.id]="dashLang.section.toolbox.style.section">
                    <div [attr.id]="dashLang.section.toolbox.style.body" class="toolbox">
                        <div [attr.id]="dashLang.section.toolbox.style.instructions">
                            <div class="row">
                                <div
                                    id="toolboxInstructionsText"
                                    class="col-xs-32 col-md-34 col-lg-36 c-margin-top--half c-margin-bottom--half"
                                >
                                    <span>{{ dashLang.section.toolbox.text.instructions.customize }}</span>
                                    <br />
                                    <span>{{ dashLang.section.toolbox.text.instructions.remove }}</span>
                                    <span>{{ dashLang.section.toolbox.text.instructions.add }}</span>
                                    <span>{{ dashLang.section.toolbox.text.instructions.keyboard }}</span>
                                </div>
                                <div
                                    class="col-xs-16 col-md-14 col-lg-12 buttons c-margin-top--half c-margin-bottom--half"
                                >
                                    <button
                                        type="button"
                                        [attr.id]="dashLang.section.toolbox.style.button.reset"
                                        class="aw-button aw-button--outline"
                                        aria-describedby="toolboxInstructionsText"
                                        (click)="onResetClick()"
                                    >
                                        {{ dashLang.section.toolbox.text.button.reset }}
                                    </button>
                                    <button
                                        type="button"
                                        [attr.id]="dashLang.section.toolbox.style.button.done"
                                        class="aw-button aw-button--primary"
                                        (click)="closeToolbox()"
                                    >
                                        {{ dashLang.section.toolbox.text.button.done }}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-md-48 c-margin-bottom--half">
                                <s25-ng-dash-section-regular
                                    [section]="section()"
                                    [customize]="customize()"
                                    [isToolbox]="true"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            }
        </div>
    `,
})
export class HomeDashSectionToolboxComponent {
    readonly section = input.required<HomeDashSectionModel>();
    readonly customize = input.required<HomeDashCustomize>();
    readonly resetDash = output<void>();

    private readonly ngZone = inject(NgZone);
    private readonly destroyRef = inject(DestroyRef);

    readonly dashLangResource = s25Resource(() => DashLangService.getLang());

    constructor() {
        const unsubscribe = DashSectionToolboxBridge.subscribeOpen(() => {
            this.ngZone.run(() => this.openToolbox());
        });
        this.destroyRef.onDestroy(unsubscribe);
    }

    closeToolbox(): void {
        this.customize().selectedMovePanelId.set(undefined);
        this.customize().toolboxOpen.set(false);
    }

    async onResetClick(): Promise<void> {
        const message = this.dashLangResource.value()?.section.toolbox.text.reset ?? "";
        if (!message) {
            return;
        }
        const confirmed = await AlertService.confirm(message);
        if (confirmed) {
            this.resetDash.emit();
        }
    }

    private openToolbox(): void {
        this.customize().toolboxOpen.set(true);
        setTimeout(() => {
            const firstButton = document.getElementById("dashSectionToolbox")?.querySelector(".buttons button");
            if (firstButton instanceof HTMLElement) {
                firstButton.focus();
            }
        }, 50);
    }
}
